import { useMemo, useState } from 'react';
import { Box, Button, Typography } from '@mui/material';
import {
  ActivityStatusBox,
  type ChipVariants,
} from './components/activity-status-box';
import { FinishPlanDialog } from './components/finish-plan-dialog';
import { InvalidPlanDialog } from './components/invalid-plan-dialog';
import { StartPlanDialog } from './components/start-plan-dialog';
import { ActivityProgress } from '@/components/partial/idp-status-cards/components/activity-progress';
import { ContentBox } from '@/components/shared/content-box';
import {
  ActivityStatus,
  PlanStatus,
  type Activity,
} from '@/data/idp/plans/interfaces';
import { activitiesAmountByStatus } from '@/utils/activities-amount-by-status';

const statusChip: Record<PlanStatus, { label: string; variant: ChipVariants }> =
  {
    [PlanStatus.Draft]: { label: 'Plano em rascunho', variant: 'todo' },
    [PlanStatus.InProgress]: {
      label: 'Plano em andamento',
      variant: 'progress',
    },
    [PlanStatus.Closed]: { label: 'Plano concluído', variant: 'concluded' },
  };

const isPlanValid = (
  activities: Activity[],
  durationInMonths: number | null
) => {
  const hasActivities = activities.length > 0;
  const hasDuration = durationInMonths !== null;
  return hasActivities && hasDuration;
};

interface StatusProps {
  id?: string;
  status: PlanStatus;
  activities: Activity[];
  durationInMonths: number | null;
}

export const Status = ({
  id,
  status,
  activities,
  durationInMonths,
}: StatusProps) => {
  const [showFinishPlanDialog, setShowFinishPlanDialog] = useState(false);
  const [showStartPlanDialog, setShowStartPlanDialog] = useState(false);
  const [showValidationDialog, setShowValidationDialog] = useState(false);

  const handleStartPlan = () => {
    if (isPlanValid(activities, durationInMonths)) {
      setShowStartPlanDialog(true);
    } else {
      setShowValidationDialog(true);
    }
  };

  const activitiesSeparatedByStatus = useMemo(() => {
    return activitiesAmountByStatus(activities);
  }, [activities]);

  return (
    <ContentBox width={{ md: 384 }} gap={2}>
      <ActivityStatusBox
        label={statusChip[status].label}
        variant={statusChip[status].variant}
      />
      {status === PlanStatus.Draft ? (
        <>
          <Box bgcolor="primary.light" p={2} borderRadius={1}>
            <Typography>
              Preencha o plano com as atividades desejadas antes de iniciá-lo.
              Após isso, acompanhe e atualize seu progresso, podendo editá-lo
              sempre que precisar
            </Typography>
          </Box>
          <Button
            variant="contained"
            onClick={handleStartPlan}
            className="media-print-hide"
          >
            Iniciar plano
          </Button>
        </>
      ) : (
        <ActivityProgress
          data={{
            [ActivityStatus.Completed]:
              activitiesSeparatedByStatus[ActivityStatus.Completed],
            [ActivityStatus.InProgress]:
              activitiesSeparatedByStatus[ActivityStatus.InProgress],
            [ActivityStatus.Pending]:
              activitiesSeparatedByStatus[ActivityStatus.Pending],
          }}
        />
      )}
      {status === PlanStatus.InProgress && (
        <>
          <Button
            variant="contained"
            onClick={() => setShowFinishPlanDialog(true)}
            className="media-print-hide"
          >
            Concluir plano
          </Button>
          <Typography variant="body2">
            Conclua o plano para sinalizar que você alcançou seus objetivos e
            está pronto para encerrar esta jornada!
          </Typography>
        </>
      )}
      {showFinishPlanDialog && (
        <FinishPlanDialog
          id={id!}
          onClose={() => setShowFinishPlanDialog(false)}
        />
      )}
      {showStartPlanDialog && (
        <StartPlanDialog
          id={id!}
          onClose={() => setShowStartPlanDialog(false)}
        />
      )}
      {showValidationDialog && (
        <InvalidPlanDialog onClose={() => setShowValidationDialog(false)} />
      )}
    </ContentBox>
  );
};
